import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "setup-mcp-claude-desktop";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title:
						"How to Use MCP Image Generation in Claude Desktop - AgentFramer",
				},
				{
					name: "description",
					content:
						"A real walkthrough for adding AgentFramer to Claude Desktop on macOS, Windows, and Linux: config paths, OAuth, debug logs, and the prompts that actually work.",
				},
				{
					property: "og:title",
					content: "How to Use MCP Image Generation in Claude Desktop",
				},
				{
					property: "og:description",
					content:
						"Configure AgentFramer in Claude Desktop end-to-end: full config example, OAuth flow, troubleshooting, and prompt patterns.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					How to use MCP image generation in Claude Desktop
				</h1>
				<p className="text-sm text-muted-foreground">
					Claude Desktop reads images. It does not generate them. With one
					config edit and a restart, AgentFramer fills the gap and Claude can
					produce stills, video, and voiceover without leaving the chat.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					What you are wiring up
				</h2>
				<p>
					Claude Desktop speaks the Model Context Protocol. Any compliant server
					can plug in and expose tools the model can call inline. AgentFramer is
					a hosted, URL-based MCP server, so there is no daemon to run and no
					Node binary to install. After setup, Claude gains a handful of tools:{" "}
					<code>generate_image</code>, <code>generate_video</code>,{" "}
					<code>generate_audio</code>, <code>get_generation</code>,{" "}
					<code>get_credits</code>, <code>list_recent_generations</code>,{" "}
					<code>list_workspaces</code>, and <code>switch_workspace</code>. The
					whole set is documented in the{" "}
					<Link
						to="/help/mcp-tools"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP tools reference
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 1: get your MCP URL or sign in with OAuth
				</h2>
				<p>
					You have two ways to authenticate. The fastest is the workspace URL:
					sign up at{" "}
					<Link
						to="/signup"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						agentframer.com
					</Link>
					, open the dashboard, and copy the MCP URL. The token is embedded in
					the path, so the URL itself is the credential, treat it like one.
				</p>
				<p>
					The second option is OAuth. Claude Desktop now supports OAuth
					discovery for remote MCP servers: point it at{" "}
					<code>https://agentframer.com/api/mcp</code> with no token, and on
					first connection Claude opens a browser window, you sign in, pick a
					workspace, and Claude Desktop stores the resulting token in its own
					keychain entry. Use OAuth if you do not want a long-lived token sat
					inside a JSON file, or if you expect to rotate workspaces often.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 2: find the config file for your OS
				</h2>
				<p>
					Claude Desktop reads MCP servers from a single JSON file. Quit the app
					fully before editing, otherwise it will overwrite your changes on
					exit.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">macOS:</strong>{" "}
						<code>
							~/Library/Application Support/Claude/claude_desktop_config.json
						</code>
					</li>
					<li>
						<strong className="text-foreground">Windows:</strong>{" "}
						<code>%APPDATA%\Claude\claude_desktop_config.json</code>
					</li>
					<li>
						<strong className="text-foreground">Linux:</strong>{" "}
						<code>~/.config/Claude/claude_desktop_config.json</code>
					</li>
				</ul>
				<p>
					If the file does not exist yet, create it. The folder will already be
					there once you have launched Claude Desktop at least once. On Windows
					you can paste the path straight into Explorer's address bar.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 3: write the config
				</h2>
				<p>
					Here is a complete config showing AgentFramer alongside two other
					servers. The shape matters more than the contents, AgentFramer is
					happy to coexist with any other stdio or URL-based MCP server you
					already use.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`{
  "mcpServers": {
    "agentframer": {
      "url": "https://agentframer.com/api/mcp/wsk_live_8a2c...your-token-here"
    },
    "filesystem": {
      "command": "npx",
      "args": [
        "-y",
        "@modelcontextprotocol/server-filesystem",
        "/Users/you/Projects"
      ]
    },
    "github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_PERSONAL_ACCESS_TOKEN": "ghp_..."
      }
    }
  }
}`}
				</pre>
				<p>
					For the OAuth flow, drop the token segment and let Claude Desktop
					handle discovery:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`{
  "mcpServers": {
    "agentframer": {
      "url": "https://agentframer.com/api/mcp"
    }
  }
}`}
				</pre>
				<p>
					Save the file, then fully quit Claude Desktop. On macOS that means
					Cmd+Q or right-click the dock icon and pick Quit, closing the window
					is not enough. On Windows, right-click the tray icon and pick Exit.
					Relaunch the app.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 4: confirm the connection
				</h2>
				<p>
					Open a new chat. The plug icon below the message input now lists
					connected MCP servers. Click it and you should see "agentframer" with
					a green dot and the eight tools listed underneath. If you used OAuth,
					the first click triggers the browser flow, you authenticate, and the
					tools appear once Claude Desktop receives the token.
				</p>
				<p>
					A quick smoke test: ask Claude how many credits you have. It should
					call <code>get_credits</code> and return a number. If you see the tool
					fire and return a value, you are wired up correctly.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 5: a real prompt sequence
				</h2>
				<p>
					The pattern that gets the best results is generate, verify, re-prompt.
					Try this verbatim:
				</p>
				<p className="text-foreground">
					"Generate a 1200x630 OG image for a blog post titled 'Async media
					pipelines'. Black background, single thin orange line forming an
					abstract waveform across the middle, small white serif title bottom
					left. Render it, then look at the result and tell me where it misses."
				</p>
				<p>
					Claude calls <code>generate_image</code>, polls{" "}
					<code>get_generation</code> until the job completes, displays the
					image inline, and because you asked for a critique it actually reads
					the image back as input. From there: "Tighten the waveform amplitude
					and move the title up 40px" produces a follow-up generation that
					inherits the prompt context. For longer-running jobs like video,
					Claude returns the generation id and you can ask it to check back
					later. More patterns are listed in the{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool call patterns guide
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Working across multiple workspaces
				</h2>
				<p>
					If you keep separate workspaces for client work, personal projects,
					and a team account, you do not need three config entries. Ask Claude
					to call <code>list_workspaces</code>, then{" "}
					<code>switch_workspace</code> with the id you want. The switch is
					session-scoped, so subsequent generations bill against the new
					workspace until you switch again or restart the app. This keeps
					credits, asset libraries, and team permissions cleanly separated
					without juggling tokens.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Troubleshooting
				</h2>
				<p>
					Most setup issues fall into four buckets, and they look almost
					identical from the chat side: the plug icon shows nothing, or shows
					AgentFramer with a red dot.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">Typo in the URL.</strong> The
						token is part of the path, a single missing character means a 404.
						Copy the URL from the dashboard with the copy button rather than
						selecting it by hand.
					</li>
					<li>
						<strong className="text-foreground">Partial restart.</strong>{" "}
						Closing the window does not unload the MCP runtime. Quit fully, then
						relaunch.
					</li>
					<li>
						<strong className="text-foreground">JSON syntax errors.</strong>{" "}
						Trailing commas after the last entry break the parse. Run the file
						through a linter or paste it into a JSON validator before saving.
					</li>
					<li>
						<strong className="text-foreground">Wrong path for the OS.</strong>{" "}
						Linux users in particular often try the macOS path first. Double
						check against the list above.
					</li>
				</ul>
				<p>
					When in doubt, read the logs. Claude Desktop writes per-server MCP
					logs to a known location:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">macOS:</strong>{" "}
						<code>~/Library/Logs/Claude/mcp-server-agentframer.log</code>
					</li>
					<li>
						<strong className="text-foreground">Windows:</strong>{" "}
						<code>%APPDATA%\Claude\Logs\mcp-server-agentframer.log</code>
					</li>
				</ul>
				<p>A healthy startup looks like this:</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">
					{`[info] Initializing server "agentframer"
[info] Connecting to https://agentframer.com/api/mcp/...
[info] Server "agentframer" connected, capabilities: tools
[info] Discovered 8 tools: generate_image, generate_video,
       generate_audio, get_generation, get_credits,
       list_recent_generations, list_workspaces, switch_workspace
[info] Server "agentframer" ready`}
				</pre>
				<p>
					A 401 in the log means the token is wrong or revoked. A 404 means the
					URL is malformed. <code>ECONNREFUSED</code> or DNS errors point at a
					corporate proxy or VPN intercepting the request. You can also enable
					Developer mode from the Help menu to expose MCP traffic in the
					devtools console. The full troubleshooting checklist lives in the{" "}
					<Link
						to="/help/mcp"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP help section
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When Claude Desktop is the wrong client
				</h2>
				<p>
					Claude Desktop is great for ideation, one-off assets, and visual
					iteration where you want to see the result and react to it. It is the
					wrong client for two scenarios.
				</p>
				<p>
					If you are working inside a repo, Cursor and Claude Code understand
					your file tree, can write generated assets straight to{" "}
					<code>public/</code>, and can wire image paths into JSX without you
					copy-pasting URLs. The same MCP URL works there, the difference is
					context, the IDE clients see your code and can act on it.
				</p>
				<p>
					If you are running a batch, say two hundred product shots from a CSV,
					or generating thumbnails on every CMS publish, drive AgentFramer
					programmatically instead. The REST API and SDK skip the chat layer
					entirely, support concurrency, and let you handle retries and storage
					on your own terms. Pricing for both paths is the same per generation,
					see the{" "}
					<Link
						to="/pricing"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						pricing page
					</Link>
					.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					You are done
				</h2>
				<p>
					Two minutes of config, one full restart, and Claude Desktop turns into
					a media workstation. The chat that used to read screenshots can now
					produce them, iterate on them, and check your credit balance before
					the next batch. Open a new chat, ask for an image, and watch the plug
					icon do its job.
				</p>
			</div>
		</article>
	);
}
